import React, { useState, useEffect } from "react";
import { Col, Row, Button, Table, Input, Popconfirm, Form, Space, message } from "antd";
import { ReactComponent as SaveIcon } from "../../../assets/save-icon.svg";
import { ReactComponent as EditIcon } from "../../../assets/edit-icon.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/delete-icon.svg";
import { ReactComponent as CrossIcon } from "../../../assets/cross-icon.svg";
import { PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { 
  setProjectPrioritiesIsEdit, 
  updateProjectPriorities, 
  updateProject,
  fetchProjectPriorities,
  createProjectPriority,
  updateProjectPriority,
  deleteProjectPriority,
  sortProjectPriorities
} from "../redux/actions";
import SpinnerComponent from "../../../components/spinner";
import { arrayMoveImmutable } from "array-move";
import { SortableContainer, SortableElement, SortableHandle } from "react-sortable-hoc";
import { Colorpicker } from "antd-colorpicker";
import "./projectSections.css";

const SortableItem = SortableElement((props) => <tr {...props} />);
const SortableBody = SortableContainer((props) => <tbody {...props} />);
const DragHandle = SortableHandle(() => <span className="priority-order-button">≡</span>);

const ProjectPrioritiesSection = ({ isProjectUpdate, canUpdate }) => {
  const [isEdit, setIsEdit] = useState(true);
  const [loading, setLoading] = useState(false);
  const [priorities, setPriorities] = useState([]);
  const [editingKey, setEditingKey] = useState("");
  const [form] = Form.useForm();
  
  const dispatch = useDispatch();

  const {
    updatePrioritiesLoading,
    currentProjectObj,
    projectPrioritiesIsEdit,
    projectPriorities: reduxPriorities,
    fetchPrioritiesLoading,
    createPriorityLoading,
    updatePriorityLoading,
    deletePriorityLoading,
    sortPrioritiesLoading
  } = useSelector((state) => state.projectsSetupReducer);

  // useEffect(() => {
  //   // Always start in view mode (not edit mode) regardless of whether it's a new or existing project
  //   setIsEdit(false);
  //   dispatch(setProjectPrioritiesIsEdit(false));
  // }, [dispatch]);
  
  // Still listen for changes to projectPrioritiesIsEdit after initial render
  useEffect(() => {
    dispatch(setProjectPrioritiesIsEdit(true));
  }, [dispatch]);

  useEffect(() => {
    const isAnyLoading = updatePrioritiesLoading || fetchPrioritiesLoading || 
                       createPriorityLoading || updatePriorityLoading || 
                       deletePriorityLoading || sortPrioritiesLoading;
    setLoading(isAnyLoading);
  }, [updatePrioritiesLoading, fetchPrioritiesLoading, createPriorityLoading, 
      updatePriorityLoading, deletePriorityLoading, sortPrioritiesLoading]);

  useEffect(() => {
    if (currentProjectObj && currentProjectObj.id) {
      // Fetch project priorities using the Redux action
      dispatch(fetchProjectPriorities(currentProjectObj.id));
    } else {
      // Reset priorities when no project is selected or when adding a new project
      setPriorities([]);
    }
  }, [currentProjectObj, dispatch]);
  
  // Update local state when Redux priorities change
  useEffect(() => {
    if (reduxPriorities && reduxPriorities.length > 0) {
      // Map the API response to the format expected by the component
      const mappedPriorities = reduxPriorities.map(priority => ({
        id: priority.id,
        priorityName: priority.name || '',
        color: priority.color || '#D9D9D9',
        order: priority.position || 0,
      }));
      
      // Sort priorities by position
      mappedPriorities.sort((a, b) => a.order - b.order);
      
      setPriorities(mappedPriorities);
    } else {
      // If no priorities exist in Redux, set an empty array
      // This ensures we don't use stale priorities from currentProjectObj
      setPriorities([]);
    }
  }, [reduxPriorities]);
  
  const isEditing = (record) => record.id === editingKey;

  const handleSave = () => {
    // if (priorities.length > 0) {
    //   // Create a mapping of priority IDs to their positions for the sort endpoint
    //   const priorityOrder = {};
    //   priorities.forEach((priority) => {
    //     if (priority.id) {
    //       priorityOrder[priority.id] = priority.order;
    //     }
    //   });
      
    //   // Dispatch sortProjectPriorities action to update priority positions
    //   if (Object.keys(priorityOrder).length > 0) {
    //     dispatch(sortProjectPriorities({
    //       priorityOrder,
    //       projectId: currentProjectObj.id
    //     }));
    //   }
      
    //   // Set project priorities edit mode to false to exit edit mode
    //   dispatch(setProjectPrioritiesIsEdit(false));
    // }
    setEditingKey("");
  };

  const handleAddPriority = () => {
    if (editingKey !== "") return;
    
    // Create a temporary ID for UI purposes (negative to avoid conflicts with real IDs)
    const tempId = -Date.now();
    const newOrder = priorities.length > 0 ? Math.max(...priorities.map(p => p.order)) + 1 : 1;
    
    const newPriority = { 
      id: tempId, 
      priorityName: "", 
      color: "#D9D9D9", 
      order: newOrder,
      isAdd: true,
      isNew: true // Flag to indicate this is a new priority that hasn't been saved to the server yet
    };
    
    setPriorities([...priorities, newPriority]);
    edit(newPriority);
  };

  const handleDeletePriority = (record) => {
    // If it's a new priority that hasn't been saved to the server yet
    if (record.isNew) {
      setPriorities(priorities.filter(p => p.id !== record.id));
      return;
    }
    
    // Dispatch deleteProjectPriority action for existing priorities
    dispatch(deleteProjectPriority(record.id));
    window.location.reload();
  };
  
  const edit = (record) => {
    form.setFieldsValue({
      priorityName: record.priorityName || record.name || "",
      color: record.color || "#D9D9D9",
    });
    setEditingKey(record.id);
  };

  const cancel = (record) => {
    form.resetFields();
    setEditingKey("");
    if (record?.isAdd) {
      setPriorities(priorities.filter(p => p.id !== record.id));
    }
  };
  
  const save = async (id) => {
    try {
      const row = await form.validateFields();
      const newPriorities = [...priorities];
      const index = newPriorities.findIndex(item => id === item.id);
      
      if (index > -1) {
        const item = newPriorities[index];
        const colorValue = row.color.hex || row.color;
        
        // Check if priorityName already exists
        const nameExists = newPriorities.some(
          p => p.id !== id && (p.priorityName || p.name || '').toLowerCase() === row.priorityName.toLowerCase()
        );
        
        if (nameExists) {
          message.error("Priority name already exists!");
          return;
        }
        
        // If it's a new priority, create it via Redux action
        if (item.isNew) {
          dispatch(createProjectPriority({
            name: row.priorityName,
            color: colorValue,
            position: item.order,
            projectId: currentProjectObj.id
          }));
        } 
        // Normal update for existing priorities
        else {
          // Update existing priority via Redux action
          dispatch(updateProjectPriority({
            id: item.id,
            name: row.priorityName,
            color: colorValue,
            position: item.order
          }));
          
          // Update the local state immediately for better UX
          const updatedItem = { 
            ...item, 
            priorityName: row.priorityName,
            color: colorValue,
            isAdd: false
          };
          
          newPriorities[index] = updatedItem;
          setPriorities(newPriorities);
        }
        
        setEditingKey("");
      }
    } catch (errInfo) {
      console.log('Validate Failed:', errInfo);
    }
  };
  
  const onSortEnd = ({ oldIndex, newIndex }) => {
    if (oldIndex !== newIndex) {
      const newData = arrayMoveImmutable(
        priorities.slice(),
        oldIndex,
        newIndex
      ).filter((el) => !!el);
      
      // Update order values
      newData.forEach((p, idx) => {
        p.order = idx;
      });
      
      setPriorities(newData);
    }
  };

  const EditableCell = ({
    editing,
    dataIndex,
    title,
    inputType,
    record,
    index,
    children,
    ...restProps
  }) => {
    let inputNode;
    
    if (dataIndex === 'priorityName') {
      inputNode = <Input placeholder="Insert Priority Name" />;
    } else if (dataIndex === 'color') {
      inputNode = <Colorpicker popup value={record?.color || "#D9D9D9"} />;
    }
    
    return (
      <td {...restProps}>
        {editing ? (
          <Form.Item
            name={dataIndex}
            style={{ margin: 0 }}
            rules={[{ required: true, message: `Please input ${title}!` }]}
          >
            {inputNode}
          </Form.Item>
        ) : (
          children
        )}
      </td>
    );
  };
  
  const DraggableContainer = (props) => (
    <SortableBody
      useDragHandle
      disableAutoscroll
      helperClass="row-dragging"
      onSortEnd={onSortEnd}
      {...props}
    />
  );

  const DraggableBodyRow = ({ className, style, ...restProps }) => {
    const index = priorities.findIndex(
      (x) => x.id === restProps["data-row-key"]
    );
    return <SortableItem index={index} {...restProps} />;
  };
  
  const columns = [
    {
      title: "PRIORITY NAME",
      dataIndex: "priorityName",
      key: "priorityName",
      editable: true,
      width: "40%",
      render: (text, record) => {
        return record.priorityName || record.name || '';
      },
    },
    {
      title: "COLOR",
      dataIndex: "color",
      key: "color",
      editable: true,
      width: "25%",
      render: (color) => (
        <div style={{ pointerEvents: "none" }}>
          <Colorpicker
            blockStyles={{ border: "none" }}
            value={color || ""}
            popup
            picker={null}
          />
        </div>
      ),
    },
    {
      title: "ACTION",
      key: "action",
      width: "20%",
      render: (_, record) => {
        const editable = isEditing(record);
        return editable ? (
          loading ? (
            <SpinnerComponent fontSize={14} />
          ) : (
            <Space size={20} align="center">
              <SaveIcon
                className="custom-icon"
                onClick={() => save(record.id)}
              />
              <Popconfirm title="Are you sure you want to cancel?" 
                onConfirm={() => cancel(record)}
                okText="Yes"
                cancelText="No"
              >
                <CrossIcon
                  style={{ height: "20px", width: "20px" }}
                  className="custom-icon"
                />
              </Popconfirm>
            </Space>
          )
        ) : (
          <Space size={20} align="center">
            {isEdit && (
              <>
                <EditIcon
                  className={`custom-icon ${editingKey !== "" ? "disable-icon" : ""}`}
                  onClick={() => editingKey === "" && edit(record)}
                />
                <Popconfirm
                  title="Are you sure you want to delete this Priority?"
                  onConfirm={() => handleDeletePriority(record)}
                  okText="Yes"
                  cancelText="No"
                  disabled={editingKey !== ""}
                >
                  <DeleteIcon className={`custom-icon ${editingKey !== "" ? "disable-icon" : ""}`} />
                </Popconfirm>
              </>
            )}
          </Space>
        );
      },
    },
    {
      title: "ORDER",
      key: "order",
      width: "15%",
      className: "drag-visible",
      render: (_, record) => {
        return !Boolean(editingKey) && isEdit && <DragHandle />;
      },
    },
  ];
  
  const mergedColumns = columns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record) => ({
        record,
        inputType: col.dataIndex === 'color' ? 'color' : 'text',
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  return (
    <>
      <Row>
        <Col span={24} className="heading-col">
          <Row align="middle" justify="space-between">
            <Col>
              <div>
                <span className="heading-span">PROJECT PRIORITIES</span>
              </div>
            </Col>
            <Col>
              {/* {isEdit ? (
                loading ? (
                  <SpinnerComponent fontSize={14} />
                ) : (
                  <SaveIcon width={16} height={16} onClick={handleSave} />
                )
              ) : (
                canUpdate && (
                  <EditIcon
                    width={16}
                    fill="#f25003"
                    height={16}
                    onClick={() => dispatch(setProjectPrioritiesIsEdit(true))}
                  />
                )
              )} */}
            </Col>
          </Row>
        </Col>
        <Col
          span={24}
          className={`user-details-section project-details-section ${
            !isEdit && "is-edit"
          }`}
        >
          <Row justify="center">
            <Col md={24} lg={23} xl={20} xxl={16}>
              <Form form={form} component={false}>
                <Table
                  components={{
                    body: {
                      cell: EditableCell,
                      wrapper: DraggableContainer,
                      row: DraggableBodyRow,
                    },
                  }}
                  className="special-table priority-table"
                  dataSource={priorities}
                  columns={mergedColumns}
                  rowClassName="editable-row"
                  pagination={false}
                  rowKey="id"
                />
                {isEdit && (
                  <Row className="priority-add-row">
                    <Col span={24}>
                      <Button
                        icon={<PlusOutlined />}
                        className="add-priority-btn"
                        onClick={handleAddPriority}
                        disabled={Boolean(editingKey)}
                      >
                        PRIORITY
                      </Button>
                    </Col>
                  </Row>
                )}
              </Form>
            </Col>
          </Row>
        </Col>
      </Row>
      <div className="empty-div"></div>
    </>
  );
};

export default ProjectPrioritiesSection;
